import React from 'react';
import { useChatContext } from 'stream-chat-react';
import { useStreamChat } from '../../../context/StreamChatContext';
import { Users } from 'lucide-react';
import UserStatusIndicator from '../UserStatusIndicator';
import styles from '../Chat.module.css';

// Avatar gradient palette using Lodgeic design tokens
const AVATAR_COLORS = [
  'var(--lodge-orange-400)',
  '#7C5CFC',  // purple
  '#12B76A',  // green
  '#0BA5EC',  // blue
  '#F04438',  // red
  '#F79009',  // amber
  '#6172F3',  // indigo
  '#EE46BC',  // pink
];

const getAvatarColor = (id) => {
  const hash = (id || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const CustomChannelListItem = ({ channel, isActive, onClick, channelName }) => {
  const { client } = useChatContext();
  const { getUserOnlineStatus } = useStreamChat();

  const getChannelDisplayName = () => {
    if (channelName) return channelName;

    const members = Object.values(channel?.state?.members || {});
    const otherMembers = members.filter(member => member.user?.id !== client?.user?.id);

    if (otherMembers.length === 1) {
      return otherMembers[0].user?.name || otherMembers[0].user?.id;
    } else if (otherMembers.length > 1) {
      const firstName = otherMembers[0].user?.name || otherMembers[0].user?.id;
      const shortFirstName = firstName.split(' ')[0];

      if (otherMembers.length === 2) {
        const secondName = otherMembers[1].user?.name || otherMembers[1].user?.id;
        return `${shortFirstName}, ${secondName.split(' ')[0]}`;
      }
      return `${shortFirstName} + ${otherMembers.length - 1} others`;
    }

    return channel?.data?.name || 'Unknown';
  };

  const getLastMessage = () => {
    const lastMessage = channel?.state?.messages?.[channel.state.messages.length - 1];
    if (!lastMessage) return 'No messages yet';

    const hasPowerBIAttachment = lastMessage.attachments &&
      lastMessage.attachments.some(att => att.type === 'pbi_filter_share');

    if (hasPowerBIAttachment) return 'Shared a report view';

    const normalAttachments = (lastMessage.attachments || [])
      .filter(att => !['pbi_filter_share', 'ai_session_share'].includes(att.type));
    let attachmentPreview = null;
    if (normalAttachments.length === 1) {
      const attachment = normalAttachments[0];
      const isImage = attachment.type === 'image' || attachment.mime_type?.startsWith?.('image/');
      attachmentPreview = isImage
        ? 'Shared a photo'
        : `Shared ${attachment.title || attachment.name || 'a file'}`;
    } else if (normalAttachments.length > 1) {
      attachmentPreview = `Shared ${normalAttachments.length} files`;
    }
    const previewText = lastMessage.text || attachmentPreview || 'Message';

    const isGroupChat = Object.keys(channel?.state?.members || {}).length > 2;
    if (isGroupChat && lastMessage.user) {
      const senderName = lastMessage.user.id === client?.user?.id
        ? 'You'
        : lastMessage.user.name?.split(' ')[0] || lastMessage.user.id;
      return `${senderName}: ${previewText}`;
    }

    return previewText;
  };

  const getLastMessageTime = () => {
    const lastMessage = channel?.state?.messages?.[channel.state.messages.length - 1];
    if (!lastMessage) return '';

    const date = new Date(lastMessage.created_at);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const unreadCount = channel?.countUnread() || 0;
  const displayName = getChannelDisplayName();
  const memberCount = Object.keys(channel?.state?.members || {}).length;
  const isGroupChat = memberCount > 2;

  const getOtherUser = () => {
    const members = Object.values(channel?.state?.members || {});
    const otherMembers = members.filter(member => member.user?.id !== client?.user?.id);
    return otherMembers.length === 1 ? otherMembers[0].user : null;
  };

  const otherUser = getOtherUser();
  const { online: isOtherUserOnline } = otherUser ? getUserOnlineStatus(otherUser.id) : { online: false };
  const directProfileImage = !isGroupChat ? otherUser?.image : null;

  const getInitials = () => {
    if (isGroupChat) return displayName.substring(0, 2).toUpperCase();

    const members = Object.values(channel?.state?.members || {});
    const otherMember = members.find(member => member.user?.id !== client?.user?.id);

    if (otherMember?.user) {
      const userName = otherMember.user.name || otherMember.user.id;
      const names = userName.split(' ');
      if (names.length > 1) return `${names[0][0]}${names[1][0]}`.toUpperCase();
      return userName.substring(0, 2).toUpperCase();
    }

    const names = displayName.split(' ');
    if (names.length > 1) return `${names[0][0]}${names[1][0]}`.toUpperCase();
    return displayName.substring(0, 2).toUpperCase();
  };

  return (
    <div
      onClick={onClick}
      className={`${styles.channelItem} ${isActive ? styles.active : ''}`}
    >
      {/* Channel Avatar */}
      <div style={{ position: 'relative', flexShrink: 0, marginRight: 'var(--sp-3)' }}>
        <div
          className={styles.avatar}
          style={{ background: getAvatarColor(channel?.id) }}
        >
          {directProfileImage ? (
            <img src={directProfileImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
          ) : isGroupChat ? (
            <Users size={18} />
          ) : (
            getInitials()
          )}
        </div>

        {unreadCount > 0 && (
          <div className={styles.unreadBadge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}

        {otherUser && (
          <div style={{ position: 'absolute', bottom: -2, right: -2 }}>
            <UserStatusIndicator userId={otherUser.id} size="sm" showBorder={true} />
          </div>
        )}
      </div>

      {/* Channel Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 2 }}>
          <div style={{ flex: 1, minWidth: 0, paddingRight: 'var(--sp-2)' }}>
            <h4
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: isActive ? 'var(--lodge-onyx-900)' : 'var(--lodge-onyx-800)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={displayName}
            >
              {displayName}
            </h4>
            {isGroupChat && (
              <span style={{ fontSize: 11, color: 'var(--lodge-onyx-400)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                <Users size={11} />
                {memberCount} members
              </span>
            )}
            {otherUser && isOtherUserOnline && (
              <span style={{ fontSize: 11, color: 'var(--color-success)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                <span style={{ width: 5, height: 5, background: 'var(--color-success)', borderRadius: 'var(--radius-full)' }} />
                Online
              </span>
            )}
            {otherUser && !isOtherUserOnline && (
              <span style={{ fontSize: 11, color: 'var(--lodge-onyx-300)', marginTop: 1, display: 'block' }}>
                Away
              </span>
            )}
          </div>
          <span style={{
            fontSize: 11,
            flexShrink: 0,
            color: unreadCount > 0 ? 'var(--lodge-orange-400)' : 'var(--lodge-onyx-300)',
            fontWeight: unreadCount > 0 ? 600 : 400,
          }}>
            {getLastMessageTime()}
          </span>
        </div>

        <p style={{
          fontSize: 13,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: unreadCount > 0 ? 'var(--lodge-onyx-700)' : 'var(--lodge-onyx-400)',
          fontWeight: unreadCount > 0 ? 500 : 400,
          lineHeight: 1.4,
        }}>
          {getLastMessage()}
        </p>
      </div>
    </div>
  );
};

export default CustomChannelListItem;
